import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Eye, Home } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { UnitDto, CreateUnitDto, UpdateUnitDto, UnitStatus } from '../types';
import DataGrid from '../components/grid/DataGrid';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import { formatCurrency } from '../utils/formatters';

const UnitsPage: React.FC = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);
	const [sortField, setSortField] = useState<string>('unitNumber');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedUnit, setSelectedUnit] = useState<UnitDto | null>(null);
	const [statusFilter, setStatusFilter] = useState<UnitStatus | 'all'>('all');
	const [bedroomsFilter, setBedroomsFilter] = useState<number | 'all'>('all');
	const [clientLocationFilter, setClientLocationFilter] = useState<number | 'all'>('all');
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	// Fetch units data
	const {
		data: unitsResponse,
		isLoading,
		error,
		refetch
	} = useQuery({
		queryKey: ['units', currentPage, pageSize, searchTerm, sortField, sortDirection, statusFilter, bedroomsFilter, clientLocationFilter],
		queryFn: () => apiService.getUnits(
			currentPage,
			pageSize,
			clientLocationFilter !== 'all' ? clientLocationFilter : undefined,
			statusFilter !== 'all' ? statusFilter : undefined,
			bedroomsFilter !== 'all' ? bedroomsFilter : undefined,
			searchTerm,
			sortField,
			sortDirection
		),
	});

	// Fetch client locations for filter
	const { data: clientLocationsResponse } = useQuery({
		queryKey: ['clientLocations'],
		queryFn: async () => {
			// Get all clients and their locations
			const clientsResponse = await apiService.getClients(1, 1000);
			const clients = clientsResponse.data?.data || [];
			const locations: any[] = [];
			for (const client of clients) {
				if (client.locations) {
					locations.push(...client.locations);
				}
			}
			return locations;
		},
	});

	const clientLocations = clientLocationsResponse || [];

	// Delete unit mutation
	const deleteUnitMutation = useMutation({
		mutationFn: (id: number) => apiService.deleteUnit(id),
		onSuccess: (response) => {
			if (response.success) {
				queryClient.invalidateQueries({ queryKey: ['units'] });
				setIsDeleteModalOpen(false);
				setSelectedUnit(null);
				toast.success('Unit deleted successfully!');
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error deleting unit:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error deleting unit: ${errorMessage}`);
		}
	});

	const handleDelete = (unit: UnitDto) => {
		setSelectedUnit(unit);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = () => {
		if (selectedUnit) {
			deleteUnitMutation.mutate(selectedUnit.unitId);
		}
	};

	const handleView = (unit: UnitDto) => {
		navigate(`/units/${unit.unitId}`);
	};

	const handleEdit = (unit: UnitDto) => {
		navigate(`/units/${unit.unitId}/edit`);
	};

	const handleAdd = () => {
		navigate('/units/create');
	};

	const getStatusDisplay = (status: UnitStatus): string => {
		const statusMap: Record<UnitStatus, string> = {
			[UnitStatus.Occupied]: 'Occupied',
			[UnitStatus.Vacant]: 'Vacant',
			[UnitStatus.VacantNeedsWork]: 'Vacant - Needs Work',
			[UnitStatus.UnderRenovation]: 'Under Renovation',
			[UnitStatus.OffMarket]: 'Off Market',
		};
		return statusMap[status] || 'Unknown';
	};

	const getStatusColor = (status: UnitStatus): string => {
		const colorMap: Record<UnitStatus, string> = {
			[UnitStatus.Occupied]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
			[UnitStatus.Vacant]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
			[UnitStatus.VacantNeedsWork]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
			[UnitStatus.UnderRenovation]: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
			[UnitStatus.OffMarket]: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
		};
		return colorMap[status] || 'bg-gray-100 text-gray-800';
	};

	const columns = useMemo(() => [
		{
			id: 'unitNumber',
			header: 'Unit Number',
			sortable: true,
			cell: (value: string, unit: UnitDto) => (
				<div className="flex flex-col">
					<span className="font-medium text-gray-900 dark:text-gray-100">{unit.unitNumber}</span>
					{unit.unitType && (
						<span className="text-sm text-gray-500 dark:text-gray-400">{unit.unitType}</span>
					)}
				</div>
			),
		},
		{
			id: 'clientLocationName',
			header: 'Property',
			sortable: true,
			cell: (value: string, unit: UnitDto) => (
				<div className="flex flex-col">
					<span className="text-gray-900 dark:text-gray-100">{unit.clientLocationName}</span>
					{unit.clientName && (
						<span className="text-sm text-gray-500 dark:text-gray-400">{unit.clientName}</span>
					)}
				</div>
			),
		},
		{
			id: 'bedrooms',
			header: 'Bed/Bath',
			cell: (value: number, unit: UnitDto) => (
				<div className="flex flex-col">
					{unit.bedrooms !== null && unit.bedrooms !== undefined && (
						<span className="text-gray-900 dark:text-gray-100">
							{unit.bedrooms} BR
							{unit.bathrooms && ` / ${unit.bathrooms} BA`}
						</span>
					)}
					{unit.squareFeet && (
						<span className="text-sm text-gray-500 dark:text-gray-400">{unit.squareFeet} sq ft</span>
					)}
				</div>
			),
		},
		{
			id: 'status',
			header: 'Status',
			sortable: true,
			cell: (value: UnitStatus, unit: UnitDto) => (
				<span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(unit.status)}`}>
					{unit.statusDisplay}
				</span>
			),
		},
		{
			id: 'monthlyRent',
			header: 'Monthly Rent',
			sortable: true,
			cell: (value: number, unit: UnitDto) => (
				<span className="text-gray-900 dark:text-gray-100">
					{unit.monthlyRent ? formatCurrency(unit.monthlyRent) : '-'}
				</span>
			),
		},
		{
			id: 'workOrderCount',
			header: 'Work Orders',
			sortable: true,
			cell: (value: number, unit: UnitDto) => (
				<div className="flex flex-col">
					<span className="text-gray-900 dark:text-gray-100">{unit.workOrderCount}</span>
					{unit.openWorkOrderCount > 0 && (
						<span className="text-sm text-red-600 dark:text-red-400">
							{unit.openWorkOrderCount} open
						</span>
					)}
				</div>
			),
		},
		{
			id: 'equipmentCount',
			header: 'Equipment',
			sortable: true,
			cell: (value: number, unit: UnitDto) => (
				<span className="text-gray-900 dark:text-gray-100">{unit.equipmentCount}</span>
			),
		},
		{
			id: 'actions',
			header: '',
			cell: (value: any, unit: UnitDto) => (
				<div className="relative text-right">
					<div className="group-hover:opacity-0 transition-opacity duration-200">
						<span className="text-gray-400">-</span>
					</div>
					<div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e) => e.stopPropagation()}>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleView(unit);
							}}
							className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
							title="View Unit"
						>
							<Eye className="h-4 w-4" />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleEdit(unit);
							}}
							className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
							title="Edit Unit"
						>
							<Edit className="h-4 w-4" />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleDelete(unit);
							}}
							className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
							title="Delete Unit"
						>
							<Trash2 className="h-4 w-4" />
						</button>
					</div>
				</div>
			),
		},
	], [handleView, handleEdit, handleDelete]);

	const units = unitsResponse?.data?.data || [];
	const totalCount = unitsResponse?.data?.totalCount || 0;
	const totalPages = Math.ceil(totalCount / pageSize);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
		setCurrentPage(1);
	};

	// Get unique bedroom counts for filter
	const uniqueBedrooms = useMemo(() => {
		const bedrooms = new Set<number>();
		units.forEach(unit => {
			if (unit.bedrooms !== null && unit.bedrooms !== undefined) {
				bedrooms.add(unit.bedrooms);
			}
		});
		return Array.from(bedrooms).sort((a, b) => a - b);
	}, [units]);

	if (error) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="text-red-600 dark:text-red-400 text-lg font-medium">Error loading units</div>
					<div className="text-gray-500 dark:text-gray-400 mt-2">Please try again</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex justify-between items-center">
				<div>
					<div className="flex items-center space-x-2">
						<Home className="h-6 w-6 text-gray-900 dark:text-gray-100" />
						<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Units</h1>
					</div>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage rental units and track occupancy</p>
				</div>
				<button
					onClick={handleAdd}
					className="flex items-center gap-2 px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors"
				>
					<Plus className="w-5 h-5" />
					Add Unit
				</button>
			</div>

			{/* Filters */}
			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						{/* Status Filter */}
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Status
							</label>
							<select
								value={statusFilter}
								onChange={(e) => {
									setStatusFilter(e.target.value as UnitStatus | 'all');
									setCurrentPage(1);
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
							>
								<option value="all">All Statuses</option>
								{Object.values(UnitStatus).filter(v => typeof v === 'number').map((status) => (
									<option key={status} value={status}>
										{getStatusDisplay(status as UnitStatus)}
									</option>
								))}
							</select>
						</div>

						{/* Bedrooms Filter */}
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Bedrooms
							</label>
							<select
								value={bedroomsFilter}
								onChange={(e) => {
									setBedroomsFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
									setCurrentPage(1);
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
							>
								<option value="all">All Bedrooms</option>
								{uniqueBedrooms.map((bedrooms) => (
									<option key={bedrooms} value={bedrooms}>
										{bedrooms} {bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}
									</option>
								))}
							</select>
						</div>

						{/* Location Filter */}
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Property
							</label>
							<select
								value={clientLocationFilter}
								onChange={(e) => {
									setClientLocationFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
									setCurrentPage(1);
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
							>
								<option value="all">All Properties</option>
								{clientLocations.map((location: any) => (
									<option key={location.id} value={location.id}>
										{location.locationName}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				{/* Data Grid */}
				<DataGrid
					data={units}
					columns={columns}
					searchTerm={searchTerm}
					onSearch={setSearchTerm}
					currentPage={currentPage}
					pageSize={pageSize}
					totalCount={totalCount}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					onSort={handleSort}
					sortField={sortField}
					sortDirection={sortDirection}
					searchPlaceholder="Search units by number, type, features..."
					emptyMessage="No units found"
					isLoading={isLoading}
					getRowClassName={() => 'group'}
					onRowClick={(row) => handleView(row as UnitDto)}
				/>
			</div>

			{/* Delete Confirmation Modal */}
			<ConfirmDialog
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setSelectedUnit(null);
				}}
				onConfirm={confirmDelete}
				title="Delete Unit"
				message={`Are you sure you want to delete unit "${selectedUnit?.unitNumber}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
				isLoading={deleteUnitMutation.isPending}
			/>
		</div>
	);
};

export default UnitsPage;

